// === Загрузка файла рельефа (DEM .tif) по координатам ===
//
// Два способа задать область:
//   1) Ввести широту/долготу центра и сторону квадрата вручную в модалке
//      (кнопка «Загрузить рельеф» в настройках).
//   2) Поставить точку на карте — режим Mode.waitForDemPoint, который
//      обрабатывается в scripts/map/mapMove.ts и вызывает onDemPointPicked().
// Оба пути шлют один запрос POST /api/dem/download {lat, lon, side_km}.
// На карте область показывается квадратом (центр + сторона в км).

import * as maplibregl from "maplibre-gl";
import { map } from "scripts/map/map";
import { Mode, state } from "scripts/app";
import { MessageToast } from "scripts/ui/components/toast";
import { ERR_NETWORK } from "scripts/ui/components/errors";

const DEM_MARKER_COLOR = "#2ecc71";
const DEM_SQUARE_SOURCE = "dem-square-source";
const DEM_SQUARE_FILL = "dem-square-fill";
const DEM_SQUARE_LINE = "dem-square-line";

// Слой уже загруженного рельефа (контур блоков высот), отдельный от квадрата
// выбора — оранжевый, чтобы не путать с зелёным предпросмотром.
const DEM_COVERAGE_COLOR = "#f39c12";
const DEM_COVERAGE_SOURCE = "dem-coverage-source";
const DEM_COVERAGE_FILL = "dem-coverage-fill";
const DEM_COVERAGE_LINE = "dem-coverage-line";
let coverageShown = false;

// Маркер центра выбранной области (пересоздаётся при каждом выборе).
let demMarker: maplibregl.Marker | null = null;

function byId<T extends HTMLElement = HTMLElement>(id: string): T | null {
    return document.getElementById(id) as T | null;
}

export function initDemDownload() {
    byId("dem-btn")?.addEventListener("click", openDemModal);
    byId("dem-cancel-btn")?.addEventListener("click", closeDemModal);
    byId("dem-pick-point-btn")?.addEventListener("click", startPickPoint);
    byId("dem-download-btn")?.addEventListener("click", downloadDem);
    byId("dem-coverage-btn")?.addEventListener("click", toggleCoverage);

    // Живой предпросмотр квадрата при ручном вводе координат/стороны
    ["dem_lat", "dem_lon", "dem_side"].forEach((id) => {
        byId(id)?.addEventListener("input", updateDemPreview);
    });

    document.querySelector("#demModal .tiles-modal-close")
        ?.addEventListener("click", closeDemModal);

    const modal = byId("demModal");
    modal?.addEventListener("click", (e) => {
        if (e.target === modal) closeDemModal();
    });
}

function openDemModal() {
    const settingsModal = byId("settings-modal");
    if (settingsModal) settingsModal.style.display = "none";

    const modal = byId("demModal");
    if (modal) modal.style.display = "flex";
}

function closeDemModal() {
    const modal = byId("demModal");
    if (modal) modal.style.display = "none";
    // Панель закрыта — убираем маркер и квадрат с карты
    state.mode = Mode.justWatching;
    map.getCanvas().style.cursor = "";
    removeDemPreview();
}

// --- Способ 2: выбор точки на карте ---------------------------------------

// Панель не перекрывает карту (класс dem-panel), поэтому не закрываем её —
// пользователь видит предпросмотр квадрата, пока выбирает/подстраивает точку.
function startPickPoint() {
    state.mode = Mode.waitForDemPoint;
    map.getCanvas().style.cursor = "crosshair";
    MessageToast.show("dem-pick", "Кликните по карте, чтобы выбрать центр области");
}

/** Вызывается из mapMove.ts после клика по карте в режиме waitForDemPoint. */
export function onDemPointPicked(lat: number, lng: number) {
    const latInput = byId<HTMLInputElement>("dem_lat");
    const lonInput = byId<HTMLInputElement>("dem_lon");
    if (latInput) latInput.value = lat.toFixed(6);
    if (lonInput) lonInput.value = lng.toFixed(6);

    updateDemPreview();

    // Выходим из режима выбора и возвращаем обычный курсор
    state.mode = Mode.justWatching;
    map.getCanvas().style.cursor = "";

    openDemModal();
}

// --- Предпросмотр области на карте (маркер центра + квадрат) ---------------

/** Квадрат вокруг центра по стороне в км → GeoJSON-полигон [[lng,lat],...]. */
function squareGeoJSON(lat: number, lng: number, side_km: number) {
    const half = side_km / 2;
    const dLat = half / 111.0;
    const dLng = half / (111.0 * Math.cos((lat * Math.PI) / 180));
    const ring = [
        [lng - dLng, lat - dLat],
        [lng + dLng, lat - dLat],
        [lng + dLng, lat + dLat],
        [lng - dLng, lat + dLat],
        [lng - dLng, lat - dLat],
    ];
    return {
        type: "Feature" as const,
        geometry: { type: "Polygon" as const, coordinates: [ring] },
        properties: {},
    };
}

function drawDemSquare(lat: number, lng: number, side_km: number) {
    const data = squareGeoJSON(lat, lng, side_km);
    const existing = map.getSource(DEM_SQUARE_SOURCE) as maplibregl.GeoJSONSource | undefined;
    if (existing) {
        existing.setData(data as any);
        return;
    }
    map.addSource(DEM_SQUARE_SOURCE, { type: "geojson", data: data as any });
    map.addLayer({
        id: DEM_SQUARE_FILL,
        type: "fill",
        source: DEM_SQUARE_SOURCE,
        paint: { "fill-color": DEM_MARKER_COLOR, "fill-opacity": 0.12 },
    });
    map.addLayer({
        id: DEM_SQUARE_LINE,
        type: "line",
        source: DEM_SQUARE_SOURCE,
        paint: { "line-color": DEM_MARKER_COLOR, "line-width": 2 },
    });
}

/** Убирает маркер центра и квадрат с карты. */
function removeDemPreview() {
    demMarker?.remove();
    demMarker = null;
    if (map.getLayer(DEM_SQUARE_FILL)) map.removeLayer(DEM_SQUARE_FILL);
    if (map.getLayer(DEM_SQUARE_LINE)) map.removeLayer(DEM_SQUARE_LINE);
    if (map.getSource(DEM_SQUARE_SOURCE)) map.removeSource(DEM_SQUARE_SOURCE);
}

/** Перерисовывает маркер центра и квадрат по текущим значениям в форме. */
function updateDemPreview() {
    const lat = parseFloat((byId<HTMLInputElement>("dem_lat")?.value) || "");
    const lng = parseFloat((byId<HTMLInputElement>("dem_lon")?.value) || "");
    if (isNaN(lat) || isNaN(lng)) return;

    if (!demMarker) {
        demMarker = new maplibregl.Marker({ color: DEM_MARKER_COLOR });
    }
    demMarker.setLngLat([lng, lat]).addTo(map);

    const side = parseFloat((byId<HTMLInputElement>("dem_side")?.value) || "");
    if (!isNaN(side) && side > 0) {
        drawDemSquare(lat, lng, side);
    }
}

// --- Показ загруженного рельефа (контур блоков высот) ---------------------

async function toggleCoverage() {
    if (coverageShown) {
        removeCoverage();
        setCoverageBtnText("Показать загруженный рельеф");
        return;
    }
    try {
        const resp = await fetch("/api/dem/coverage");
        const fc = await resp.json();
        if (!resp.ok || !fc.features || fc.features.length === 0) {
            MessageToast.show("dem-cov", "Рельеф ещё не загружен");
            return;
        }
        drawCoverage(fc);
        setCoverageBtnText("Скрыть загруженный рельеф");
        // Подлетаем к покрытию, чтобы его было видно
        const b = coverageBounds(fc);
        if (b) map.fitBounds(b, { padding: 40, duration: 600 });
    } catch (e) {
        MessageToast.showDanger("dem-cov", "Не удалось получить покрытие рельефа");
    }
}

function setCoverageBtnText(text: string) {
    const btn = byId("dem-coverage-btn");
    if (btn) btn.textContent = text;
}

function drawCoverage(fc: any) {
    const existing = map.getSource(DEM_COVERAGE_SOURCE) as maplibregl.GeoJSONSource | undefined;
    if (existing) {
        existing.setData(fc);
    } else {
        map.addSource(DEM_COVERAGE_SOURCE, { type: "geojson", data: fc });
        map.addLayer({
            id: DEM_COVERAGE_FILL,
            type: "fill",
            source: DEM_COVERAGE_SOURCE,
            paint: { "fill-color": DEM_COVERAGE_COLOR, "fill-opacity": 0.15 },
        });
        map.addLayer({
            id: DEM_COVERAGE_LINE,
            type: "line",
            source: DEM_COVERAGE_SOURCE,
            paint: { "line-color": DEM_COVERAGE_COLOR, "line-width": 2 },
        });
    }
    coverageShown = true;
}

function removeCoverage() {
    if (map.getLayer(DEM_COVERAGE_FILL)) map.removeLayer(DEM_COVERAGE_FILL);
    if (map.getLayer(DEM_COVERAGE_LINE)) map.removeLayer(DEM_COVERAGE_LINE);
    if (map.getSource(DEM_COVERAGE_SOURCE)) map.removeSource(DEM_COVERAGE_SOURCE);
    coverageShown = false;
}

/** Общий bbox всех полигонов покрытия → [[west,south],[east,north]] для fitBounds. */
function coverageBounds(fc: any): [[number, number], [number, number]] | null {
    let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity;
    for (const f of fc.features) {
        const polys = f.geometry.coordinates as number[][][];
        for (const ring of polys) {
            for (const [lng, lat] of ring) {
                if (lng < w) w = lng;
                if (lng > e) e = lng;
                if (lat < s) s = lat;
                if (lat > n) n = lat;
            }
        }
    }
    if (!isFinite(w)) return null;
    return [[w, s], [e, n]];
}

// --- Загрузка -------------------------------------------------------------

function setStatus(message: string, type: "info" | "error" | "success", busy = false) {
    const el = byId("dem-status");
    if (!el) return;
    el.style.display = "block";
    el.style.padding = "10px";
    el.style.borderRadius = "8px";
    el.style.color = "#fff";
    el.style.marginTop = "12px";
    el.style.backgroundColor =
        type === "success" ? "#1a5c3a" : type === "error" ? "#b92525" : "#3c98ee";
    el.innerHTML = busy
        ? `<div style="display:flex;align-items:center;gap:10px;">
               <div style="flex:0 0 auto;box-sizing:border-box;width:18px;height:18px;border:2px solid #fff;border-top-color:transparent;border-radius:50%;animation:spin 1s linear infinite;"></div>
               <span>${message}</span>
           </div>`
        : message;
}

async function downloadDem() {
    const lat = parseFloat((byId<HTMLInputElement>("dem_lat")?.value) || "");
    const lon = parseFloat((byId<HTMLInputElement>("dem_lon")?.value) || "");
    const side_km = parseFloat((byId<HTMLInputElement>("dem_side")?.value) || "");

    if (isNaN(lat) || isNaN(lon)) {
        setStatus("Укажите широту и долготу (или выберите точку на карте)", "error");
        return;
    }
    if (isNaN(side_km) || side_km <= 0) {
        setStatus("Укажите сторону квадрата в км (> 0)", "error");
        return;
    }

    const downloadBtn = byId<HTMLButtonElement>("dem-download-btn");
    if (downloadBtn) downloadBtn.disabled = true;
    setStatus("Загрузка рельефа… это может занять до минуты", "info", true);

    try {
        const response = await fetch("/api/dem/download", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ lat, lon, side_km }),
        });
        const result = await response.json().catch(() => ({}));

        if (response.ok) {
            setStatus(result.message || "Рельеф успешно загружен", "success");
            // Если слой покрытия открыт — обновим его с учётом новой области
            if (coverageShown) {
                fetch("/api/dem/coverage")
                    .then((r) => r.json())
                    .then((fc) => { if (fc.features?.length) drawCoverage(fc); })
                    .catch(() => {});
            }
        } else {
            setStatus(result.detail || "Не удалось загрузить рельеф", "error");
        }
    } catch (e) {
        setStatus(ERR_NETWORK, "error");
    } finally {
        if (downloadBtn) downloadBtn.disabled = false;
    }
}
